// Blocky humanoid with Minecraft-layout skin UVs and procedural animation.
import * as THREE from 'three';
import { PART_LAYOUT, faceRects } from './skins.js';
import { clamp, lerp } from './util.js';
import { swordModel, crownModel, headphonesModel, antennaModel, haloModel, nameTag } from './models.js';

export const PX = 1.8 / 32;

const geoCache = new Map();
function partGeometry(part, inflate = 0) {
  const key = part + ':' + inflate;
  if (geoCache.has(key)) return geoCache.get(key);
  const [u, v, w, h, d] = PART_LAYOUT[part];
  const g = new THREE.BoxGeometry((w + inflate) * PX, (h + inflate) * PX, (d + inflate) * PX);
  const r = faceRects(u, v, w, h, d);
  const order = [r.left, r.right, r.top, r.bottom, r.front, r.back];
  const uv = g.attributes.uv;
  for (let f = 0; f < 6; f++) {
    const [x, y, rw, rh] = order[f];
    for (let k = 0; k < 4; k++) {
      const i = f * 4 + k;
      const s = uv.getX(i), t = uv.getY(i);
      uv.setXY(i, (x + s * rw) / 64, 1 - (y + (1 - t) * rh) / 64);
    }
  }
  geoCache.set(key, g);
  return g;
}

function skinTex(canvas) {
  const t = new THREE.CanvasTexture(canvas);
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export class Character {
  constructor(skin, { name = null, crown = false, scale = 1, shadow = true, tagScale = 1, tagColor } = {}) {
    this.root = new THREE.Group();
    this.model = new THREE.Group();
    this.root.add(this.model);
    this.model.scale.setScalar(scale);
    this.scale = scale;
    this.mat = new THREE.MeshLambertMaterial({ alphaTest: 0.5 });
    this.baseColor = new THREE.Color(1, 1, 1);
    this.setSkin(skin);
    const mk = (part, inflate = 0) => {
      const m = new THREE.Mesh(partGeometry(part, inflate), this.mat);
      m.castShadow = shadow;
      m.receiveShadow = false;
      return m;
    };
    // pivots
    this.headPivot = new THREE.Group();
    this.headPivot.position.set(0, 24 * PX, 0);
    const head = mk('head');
    head.position.y = 4 * PX;
    const hat = mk('hat', 1);
    hat.position.y = 4 * PX;
    hat.castShadow = false;
    this.headPivot.add(head, hat);
    this.body = mk('body');
    this.body.position.y = 18 * PX;
    this.armR = new THREE.Group();
    this.armR.position.set(-6 * PX, 22 * PX, 0);
    const ar = mk('armR');
    ar.position.y = -4 * PX;
    this.armR.add(ar);
    this.armL = new THREE.Group();
    this.armL.position.set(6 * PX, 22 * PX, 0);
    const al = mk('armL');
    al.position.y = -4 * PX;
    this.armL.add(al);
    this.legR = new THREE.Group();
    this.legR.position.set(-2 * PX, 12 * PX, 0);
    const lr = mk('legR');
    lr.position.y = -6 * PX;
    this.legR.add(lr);
    this.legL = new THREE.Group();
    this.legL.position.set(2 * PX, 12 * PX, 0);
    const ll = mk('legL');
    ll.position.y = -6 * PX;
    this.legL.add(ll);
    this.model.add(this.headPivot, this.body, this.armR, this.armL, this.legR, this.legL);
    // hand anchor for items
    this.hand = new THREE.Group();
    this.hand.position.set(0, -10 * PX, 0.5 * PX);
    this.armR.add(this.hand);
    this.item = null;
    this.accessory = null;
    if (name) {
      this.tag = nameTag(name, { crown, scale: tagScale, color: tagColor });
      this.tag.position.y = 2.25 * scale;
      this.root.add(this.tag);
    }
    this.walk = 0;
    this.time = Math.random() * 10;
    this.rot = { lr: 0, ll: 0, ar: 0, al: 0, arz: 0, alz: 0, hx: 0, hy: 0, by: 0 };
    this.squash = 0;
    this.flash = 0;
  }

  setSkin(skin) {
    if (this.mat.map) this.mat.map.dispose();
    if (this.mat.emissiveMap) this.mat.emissiveMap.dispose();
    this.mat.map = skinTex(skin.canvas);
    if (skin.glow) {
      this.mat.emissiveMap = skinTex(skin.glow);
      this.mat.emissive = new THREE.Color(1, 1, 1);
      this.mat.emissiveIntensity = 1.2;
    } else {
      this.mat.emissiveMap = null;
      this.mat.emissive = new THREE.Color(0, 0, 0);
    }
    this.mat.needsUpdate = true;
  }

  setHeldObject(obj, block = false) {
    this.setItem(null);
    this.item = obj;
    this.holdingBlock = block;
    if (obj) this.hand.add(obj);
  }

  placeGesture() { this.placeT = 0.32; }

  setItem(kind) {
    this.holdingBlock = false;
    if (this.item) {
      this.hand.remove(this.item);
      this.item.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) o.material.dispose();
      });
      this.item = null;
    }
    if (!kind) return;
    const s = swordModel(kind);
    s.rotation.set(-0.25, -Math.PI / 2, 0);
    this.hand.add(s);
    this.item = s;
  }

  setAccessory(kind) {
    if (this.accessory) {
      this.headPivot.remove(this.accessory);
      this.accessory = null;
    }
    const m = { crown: crownModel, headphones: headphonesModel, antenna: antennaModel, halo: haloModel }[kind];
    if (m) {
      this.accessory = m();
      this.headPivot.add(this.accessory);
    }
  }

  setArmour(gear={}) {
    if(this.armour){this.armour.removeFromParent();this.armour.traverse(o=>{o.geometry?.dispose();o.material?.dispose();});}
    this.armour=new THREE.Group();this.model.add(this.armour);
    const box=(w,h,d,x,y,z,col)=>{const m=new THREE.Mesh(new THREE.BoxGeometry(w,h,d),new THREE.MeshLambertMaterial({color:col}));m.position.set(x,y,z);this.armour.add(m);};
    if(gear.armour){const c=gear.armour==='armour_diamond'?'#6be8e6':'#c4d3df';box(.55,.55,.32,0,1,0,c);box(.5,.13,.5,0,1.82,0,c);}
    if(gear.boots)for(const x of[-.12,.12])box(.24,.16,.35,x,.1,.03,'#bce2f4');
    if(gear.shield)box(.35,.5,.12,.43,.82,.1,'#b39359');
  }
  hurtFlash() {
    this.flash = 0.35;
  }

  land(strength = 1) {
    this.squash = Math.min(1, 0.6 * strength);
  }

  // s: {speed, grounded, vy, swing (-1 or 0..1), celebrate, zombie, wave, look:{x,y}}
  update(dt, s = {}) {
    this.time += dt;
    const t = this.time;
    const speed = s.speed || 0;
    const amp = clamp(speed / 6, 0, 1);
    if (amp > 0.04) this.walk += dt * (5 + speed * 1.25);
    else this.walk = lerp(this.walk, Math.round(this.walk / Math.PI) * Math.PI, 1 - Math.exp(-10 * dt));
    const sw = Math.sin(this.walk);
    const T = {
      lr: sw * 0.95 * amp,
      ll: -sw * 0.95 * amp,
      ar: -sw * 0.75 * amp,
      al: sw * 0.75 * amp,
      arz: -0.06 - Math.sin(t * 1.6) * 0.03,
      alz: 0.06 + Math.sin(t * 1.6) * 0.03,
      hx: 0,
      hy: 0,
      by: 0,
    };
    if (s.look) {
      T.hy = clamp(s.look.y, -0.9, 0.9);
      T.hx = clamp(s.look.x, -0.5, 0.5);
    }
    if (s.grounded === false) {
      const up = clamp((s.vy || 0) / 10, -1, 1);
      T.lr = -0.55 + up * 0.2;
      T.ll = 0.45 - up * 0.2;
      T.ar = -0.5 - up * 0.4;
      T.al = 0.2 - up * 0.3;
      T.arz = -0.55;
      T.alz = 0.55;
    }
    if (this.holdingBlock) {
      T.ar = -0.95 - sw * 0.12 * amp;
      T.al = -0.7 + sw * 0.1 * amp;
      T.arz = -0.1; T.alz = 0.18;
    }
    if(s.riding){T.lr=-1.15;T.ll=-1.15;T.ar=-.65;T.al=-.65;}
    if (this.placeT > 0) {
      this.placeT = Math.max(0, this.placeT - dt);
      T.ar -= Math.sin(this.placeT / .32 * Math.PI) * .75;
    }
    if (s.zombie) {
      T.ar = -1.45 + Math.sin(t * 3) * 0.08;
      T.al = -1.45 - Math.sin(t * 3) * 0.08;
      T.arz = -0.05;
      T.alz = 0.05;
    }
    if (s.celebrate) {
      T.ar = -2.9 + Math.sin(t * 12) * 0.25;
      T.al = -2.9 - Math.sin(t * 12) * 0.25;
      T.arz = -0.35;
      T.alz = 0.35;
      T.lr = Math.sin(t * 12) * 0.2;
      T.ll = -Math.sin(t * 12) * 0.2;
    }
    if (s.wave) {
      T.ar = -2.7;
      T.arz = -0.4 + Math.sin(t * 9) * 0.45;
    }
    const k = 1 - Math.exp(-16 * dt);
    const R = this.rot;
    for (const key of Object.keys(T)) R[key] += (T[key] - R[key]) * k;
    let swingBody = 0;
    let arX = R.ar;
    if (s.swing !== undefined && s.swing >= 0) {
      const p = s.swing;
      if (p < 0.28) arX = lerp(-0.4, -2.7, p / 0.28);
      else if (p < 0.5) arX = lerp(-2.7, 0.25, (p - 0.28) / 0.22);
      else arX = lerp(0.25, R.ar, (p - 0.5) / 0.5);
      swingBody = p < 0.28 ? -0.35 * (p / 0.28) : p < 0.5 ? lerp(-0.35, 0.4, (p - 0.28) / 0.22) : lerp(0.4, 0, (p - 0.5) / 0.5);
    }
    this.legR.rotation.z = s.riding ? -.25 : 0;
    this.legL.rotation.z = s.riding ? .25 : 0;
    this.legR.rotation.x = R.lr;
    this.legL.rotation.x = R.ll;
    this.armR.rotation.x = arX;
    this.armL.rotation.x = R.al;
    this.armR.rotation.z = R.arz;
    this.armL.rotation.z = R.alz;
    this.headPivot.rotation.x = R.hx;
    this.headPivot.rotation.y = R.hy;
    this.model.rotation.y = swingBody;
    // bob + squash
    this.squash = Math.max(0, this.squash - dt * 4);
    const sq = Math.sin(this.squash * Math.PI) * 0.18;
    const bob = Math.abs(Math.sin(this.walk)) * 0.07 * amp + (s.celebrate ? Math.abs(Math.sin(t * 6)) * 0.35 : 0);
    this.model.position.y = bob;
    this.model.scale.set(this.scale * (1 + sq * 0.6), this.scale * (1 - sq), this.scale * (1 + sq * 0.6));
    // hurt flash
    if (this.flash > 0) {
      this.flash -= dt;
      const f = this.flash > 0 ? 0.5 + 0.5 * Math.sin(this.flash * 40) : 0;
      this.mat.color.setRGB(1, 1 - f * 0.65, 1 - f * 0.65);
    } else this.mat.color.copy(this.baseColor);
  }

  dispose() {
    if(this.armour){this.armour.removeFromParent();this.armour.traverse(o=>{o.geometry?.dispose();o.material?.dispose();});}
    this.root.traverse((o) => {
      if (o.material && o.material !== this.mat && o.material.dispose) o.material.dispose();
    });
    if (this.mat.map) this.mat.map.dispose();
    if (this.mat.emissiveMap) this.mat.emissiveMap.dispose();
    this.mat.dispose();
    if (this.tag) {
      this.tag.material.map.dispose();
      this.tag.material.dispose();
    }
  }
}
